import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Icon from 'react-web-vector-icons';
import EditIcon from '../assets/edit.svg';
import TrashIcon from '../assets/trash.svg';

import { fetchDefaultLists, fetchListsForEntity, setDefaultList, updateList, createList, deleteList, clearListsError } from '../redux/Lists/actions';
import ListEditModal from './ListEditModal';
import { showConfirm } from './ConfirmBox';
import Toaster from './Toaster';

/**
 * CRM List View Management Interface
 * 
 * Domain Logic:
 * - Manages saved views of CRM entities (contacts, accounts, opportunities, campaigns)
 * - Preserves column layouts when switching between different CRM contexts
 * - System lists (created by admins) cannot be edited by regular users
 * - Each user can have different default views per entity type
 * - Field preservation prevents losing custom column configurations
 * 
 * @param {Object} props
 * @param {string} props.entityType - Entity type (e.g., 'contacts', 'accounts')
 * @param {Function} [props.onColumnsChange] - Callback when visible columns change
 * @param {Array<Object>} props.displayFields - Available fields for display
 * @param {Array<string>} props.defaultFields - Default visible fields
 */
const ListManager = ({ entityType , onColumnsChange, displayFields, defaultFields }) => {
  const dispatch = useDispatch();

  // Redux state
  /** @type {Array<Object>} Lists for current entity type */
  const lists = useSelector(state => state.lists.listsByEntity[entityType] || []);
  /** @type {string|number} Default list ID for entity type */
  const defaultIds = useSelector(state => state.lists.defaultIds[entityType]);
  /** @type {boolean} Loading state */
  const loading = useSelector(state => state.lists.loading);
  /** @type {string|null} Error message */
  const error = useSelector(state => state.lists.error);
  /** @type {boolean} Whether default IDs are loaded */
  const hasDefaultIds = useSelector(state => Object.keys(state.lists.defaultIds).length > 0);

  // Local state
  /** @type {boolean} Dropdown menu visibility */
  const [showMenu, setShowMenu] = useState(false);
  /** @type {string|null} Active modal type */
  const [modalType, setModalType] = useState(null);
  /** @type {Object|null} Modal data */
  const [modalData, setModalData] = useState(null);
  /** @type {Object|null} Selected list */
  const [selectedList, setSelectedList] = useState(null);
  /** @type {Array<string>} Visible fields */

  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedList(null);
  }, [entityType]);

  useEffect(() => {
    if (!hasDefaultIds) {
      dispatch(fetchDefaultLists());
    }
  }, [entityType, dispatch, hasDefaultIds]);

  useEffect(() => {
    dispatch(fetchListsForEntity(entityType));
  }, [entityType, dispatch]);

  useEffect(() => {
    if (lists.length && defaultIds) {
      const matched = lists.find(l => String(l.id) === String(defaultIds));
      if (matched) {
        setSelectedList(matched);
        const fields = getVisibleFieldsFromList(matched);
        if (onColumnsChange) onColumnsChange(fields);
      }
    }
  }, [lists, defaultIds, onColumnsChange]);

  /**
   * Extracts visible fields from list's JSON configuration
   * Falls back to default fields if parsing fails
   */
  const getVisibleFieldsFromList = (list = selectedList) => {
    if (!list?.fields_json) return defaultFields;
    try {
      const fields = JSON.parse(list.fields_json);
      return Array.isArray(fields) ? fields : defaultFields;
    } catch {
      return defaultFields;
    }
  };

  /**
   * Deletes list with permission check and confirmation
   * Shows error if list is not editable
   */
  const handleDelete = async (list) => {
    if (!list.is_editable) {
      Toaster.showError('This list cannot be deleted');
      return;
    }

    showConfirm({
      title: 'Delete List',
      message: `“${list.list_name}” will be deleted forever, are you sure?`,
      confirmButtonLabel: 'Delete',
      confirmButtonType: 'danger',
      confirmButtonIconRight: <img src={TrashIcon} alt="Trash" style={{ marginBottom: 2 }} />,
      onConfirm: async () => {
        const result = await dispatch(deleteList(entityType, list.id));
        setSelectedList(null);
        setShowMenu(false);
        setModalData(null);

        const fields = getVisibleFieldsFromList(null);
        if (onColumnsChange) onColumnsChange(fields);

        if (!result || result.status !== 'error') {
          Toaster.showSuccess('List deleted successfully');
        } else {
          Toaster.showError(result.message || 'Failed to delete list');
        }
      },
      cancelButtonLabel: 'Cancel',
      cancelButtonType: 'transparent',
    });
  };

  /**
   * Maps display fields to column selector format
   */
  const getAvailableFields = () =>
    displayFields
      // .filter(field => field.contexts.includes('all') || field.contexts.includes('list'))
      .map(field => ({ key: field.field, label: field.label }));

  const handleCreateNew = (e) => {
    e.stopPropagation();

    dispatch(clearListsError()); 
    setModalType('new'); 
    setModalData(null); 
    setShowMenu(false);
  }

  const handleGetNewVisibleFields = (newVisibleFields, currentFields) => {
    let allSavedFields = [];
    try {
        allSavedFields = JSON.parse(currentFields || '[]');
    } catch {}

    // Get preserved fields (fields not in current context)
    const currentContextFields = displayFields.map(f => f.field);
    const preservedFields = allSavedFields.filter(
        f => !currentContextFields.includes(f)
    );

    // Create a map of original positions
    const originalPositions = new Map(
        allSavedFields.map((field, index) => [field, index])
    );

    // Start with new visible fields in their selected order
    const mergedFields = [...newVisibleFields];

    // Insert preserved fields back into their original positions
    preservedFields.forEach(field => {
        const originalPosition = originalPositions.get(field);
        // Find the first visible field that was after this preserved field in the original order
        const insertAfterIndex = mergedFields.findIndex(f => 
            originalPositions.get(f) > originalPosition
        );
        
        if (insertAfterIndex === -1) {
            // If no visible field was after this preserved field, add to end
            mergedFields.push(field);
        } else {
            // Insert at the correct position
            mergedFields.splice(insertAfterIndex, 0, field);
        }
    });

    return mergedFields;
  }
  
  const handleSaveList = async (data, newVisibleFields) => {
    let result;
    if (modalType === 'new') {
      result = await dispatch(createList({
        ...data,
        entity_type: entityType,
        fields_json: JSON.stringify(handleGetNewVisibleFields(newVisibleFields, '[]'))
      }));

      if (!result || result.status !== 'error') {
        await dispatch(setDefaultList(entityType, result.id));
        Toaster.showSuccess('List created successfully');
        setModalType(null);
      } else {
        Toaster.showError(result.message || 'Failed to create list');
      }
    } else if (modalType === 'edit') {
      result = await dispatch(updateList(entityType, { 
        ...modalData, 
        ...data, 
        fields_json: JSON.stringify(handleGetNewVisibleFields(newVisibleFields, data?.fields_json))
       }));

      if (!result || result.status !== 'error') {
        Toaster.showSuccess('List updated successfully');
        setModalType(null);
      } else {
        Toaster.showError(result.message || 'Failed to update list');
      }
    }
  }

  const handleSelectList = async (list) => {
    try {
      await dispatch(setDefaultList(entityType, list.id));
      setSelectedList(list);
      const fields = getVisibleFieldsFromList(list);
      if (onColumnsChange) onColumnsChange(fields);
    } catch (e) {
      Toaster.showError(e.message || 'Failed to select list');
    }
  }

  const handleSelectUpdatedList = (list) => {
    dispatch(clearListsError()); 
    setModalType('edit'); 
    setModalData(list); 
    setShowMenu(false);
  }

  const handleDropdownClickOutside = useCallback((event) => {
		const isOutside = Array.isArray(dropdownRef)
			? dropdownRef
					.filter(r => Boolean(r.current))
					.every(r => r.current && !r.current.contains(event.target))
			: dropdownRef.current && !dropdownRef.current.contains(event.target)

		if (isOutside) {
			dispatch(clearListsError()); 
      setModalType(null); 
      setModalData(null); 
      setShowMenu(false);
		}
	}, [dispatch, dropdownRef]);

  useEffect(() => {
    document.addEventListener('mousedown', handleDropdownClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleDropdownClickOutside);
    };
  }, [handleDropdownClickOutside]);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div 
          role='button'
          title="Select list"
          style={{ opacity: loading ? 0.5 : 1,
            cursor: loading ? 'not-allowed' : 'pointer',
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '2px',
            padding: '5px 12px',
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: '#D3E1ED',
            borderTopLeftRadius: showMenu === true ? 8 : 45,
            borderTopRightRadius: showMenu === true ? 8 : 45,
            borderBottomLeftRadius: showMenu === true ? 0 : 45,
            borderBottomRightRadius: showMenu === true ? 0 : 45,
            borderBottomWidth: showMenu === true ? 0 : 1,
            minWidth: showMenu === true ? 200 : undefined,
          }}
          disabled={loading}
          onClick={() => setShowMenu(!showMenu)}
         >
          <span style={{ fontSize: 14, fontWeight: 500 }}>{selectedList?.list_name ? selectedList?.list_name : 'Select list'}</span>

          <Icon name="chevron-small-down" font="Entypo" color="#142A31" size={20} />

          {showMenu && (
            <div 
              ref={dropdownRef}
              style={{ 
                position: 'absolute',
                top: '100%',
                left: -1,
                zIndex: 1000,
                background: 'white',
                borderStyle: 'solid',
                borderWidth: 1,
                borderTopWidth: 0,
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
                borderColor: '#D3E1ED',
                minWidth: '200px',
                width: '100%',
                backgroundColor: '#F5F9FF',
             }}>
              <div style={{ padding: 0 }}>
                {lists?.length > 0 && (
                  lists.map(listItem => (
                    <MenuItem 
                      key={listItem.id}
                    >
                      <div 
                        role='button' 
                        style={{ fontWeight: selectedList?.id === listItem.id ? 700 : undefined, flexGrow: 1 }}
                        onClick={() => handleSelectList(listItem)}
                      >
                        {selectedList?.id === listItem.id && '✓ '}
                        {listItem.list_name}
                      </div>

                      <div
                        style={{ 
                          display: 'flex',
                          gap: '2px',
                          alignSelf: 'flex-start',
                        }}
                      >

                        {Boolean(listItem.is_editable) && (
                          <>
                            <img 
                              role='button'
                              src={EditIcon} 
                              alt="Edit" 
                              onClick={() => handleSelectUpdatedList(listItem)}
                            />

                            <div 
                              role='button' 
                              onClick={() => handleDelete(listItem)}
                            >
                              <Icon 
                                name="close" 
                                font="AntDesign" 
                                color="#BFD2E2" 
                                size={16}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </MenuItem>
                  ))
                )}

                <div
                  style={{
                    borderTop: '1px solid #D3E1ED',
                  }}
                >
                  <MenuItem onClick={(e) => {handleCreateNew(e)}}>
                    <div 
                      style={{
                        fontWeight: 700,
                        color: '#40A0C1',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <Icon name="plus" font="AntDesign" color="#40A0C1" size={16} />

                      Create List
                    </div>
                  </MenuItem>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {(modalType === 'new' || modalType === 'edit') && (
        <ListEditModal 
          mode={modalType}
          initial={modalData}
          availableFields={getAvailableFields()}
          error={error}
          loading={loading}
          onClose={() => setModalType(null)}
          onSave={handleSaveList}
        />
      )}
    </div>
  );
};

/**
 * Dropdown menu item with disabled state styling
 */
const MenuItem = ({ children, onClick, disabled }) => (
  <div
    style={{
      padding: '8px 12px',
      opacity: disabled ? 0.5 : 1,
      transition: 'background-color 0.2s',
      fontSize: 14,
      fontWeight: 500,
      display: 'flex',
      justifyContent: 'space-between',
      gap: '8px',
    }}
    onClick={onClick}
  >
    {children}
  </div>
);

export default ListManager;
